import React, { useEffect, useState } from 'react'
import { CustomDrawerProps, ItemType } from '../../external/ensolvers-core-frontend-mui/components/Main/AppContainer';
import { Dialog, List, useTheme, Drawer, Divider, ListItemIcon, ListItemText, ListItemButton } from '@mui/material';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary } from './MenuListAccordion';
import CommandAction from '../../external/ensolvers-core-frontend-mui/types/CommandAction';
import { ColorUtils } from '../../utils/ColorUtils';
import CommandActionSection from '../../external/ensolvers-core-frontend-mui/types/CommandActionSection';
import { STATIC_ICONS } from '../../utils/NavigationUtils';
import { drawerWidth, topBarHeight } from './AppContainerStyles';

export const CustomDrawer = ({ open, setOpen, smallViewport, customSections, menuSections, menuOptions }: CustomDrawerProps) => {
  const [activeSection, setActiveSection] = useState<CommandActionSection>();
  const [activeSectionItem, setActiveSectionItem] = useState<CommandAction>();
  const [staticCommandActions, setStaticCommandActions] = useState<ItemType>(STATIC_ICONS);
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);

  const theme = useTheme();
  const navigate = useHistory();
  const location = useLocation();

  useEffect(()=>{
    getActiveSection()
  },[location])

  const getActiveSection = () => {
    const activeSection = menuSections.filter((section) => section.items.find(item => getPathname().includes(item.actionPath)));
    if(activeSection.length <= 0)  return 
    setActiveSection(activeSection[0]);
    const activeItem = activeSection[0].items.filter(item => getPathname().includes(item.actionPath)); 
    if(activeItem.length <= 0) return
    setActiveSectionItem(activeItem[0]);
  }

  const getPathname = () => {
    return location.pathname;
  }

  // function to check if an accordion is open
  const isAccordionExpanded = (index: number) => {
    if (!openAccordions.includes(index)) {
      return false;
    } else {
      return (smallViewport ? false : true);
    }
  };

  const handleAccordionChange = (index: number) => {
    const newOpenAccordions = openAccordions.includes(index)
      ? openAccordions.filter((i) => i !== index)
      : [...openAccordions, index];
    setOpenAccordions(newOpenAccordions);
    localStorage.setItem("openAccordions", JSON.stringify(newOpenAccordions));
  };  

  const getKeyIcon = (key: string): JSX.Element => {
    return staticCommandActions[key];
  };

  const getMenuSectionList = () => (
    <>
      {menuSections.map((section, index) => {
        return (
          <Accordion defaultExpanded={isAccordionExpanded(index)} key={index} onChange={() => handleAccordionChange(index)}>
            <AccordionSummary
              activeSection={section === activeSection}
              closable={ smallViewport ? false : undefined}
              title={section.label}
              icon={section.icon}
              sx={{ whiteSpace: "pre-wrap", background: ColorUtils.LIGHTER_GRAY}}
              closed={!open}
            />
            <AccordionDetails sx={{ whiteSpace: "pre-wrap", textIndent: 4 }} style={!open ? { paddingLeft: 0 } : {}}>
              <List disablePadding>
                {section.items.map((opt: CommandAction, index) => {
                  const activeLink = activeSectionItem === opt;
                  return (
                  <NavLink
                    key={index}
                    to={opt.actionPath}
                    activeStyle={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING }}
                    style={{ textDecoration: "none", color: "gray", display: "flex", alignItems: "center" }}
                  >
                    <ListItemButton
                      onClick={() => {
                        if ( smallViewport ) {
                          setOpen(false);
                        }
                      }}
                      key={index}
                    >
                      <ListItemIcon 
                        style={activeLink ? {color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING} : {}} 
                        sx={{
                          marginRight: open ? "" : "10px",
                          color: open ? "" : "silver",
                          "&.MuiListItemIcon-root": {
                            minWidth:"35px ", 
                          },
                          '& span': {
                            fontSize: '0.9rem',
                          },
                          '& svg': {
                            fontSize: '1.4rem',
                          }
                        }}
                      >
                        {opt.icon || getKeyIcon(opt.iconKey!)}
                      </ListItemIcon>
                      <ListItemText sx={{
                        "&.MuiListItemIcon-root": {
                          minWidth:"35px ", 
                        },
                      }} primary={opt.label} />
                    </ListItemButton>
                  </NavLink>
                )})}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );

  // A function that returns the list of menu options to display in the AppDrawer
  const getMenuList = () => {
    return (
      <>
        {menuOptions.map((opt: CommandAction, index) => (
          <ListItemButton
            key={index}
            onClick={() => {
              navigate.push(opt.actionPath);
              if ( smallViewport ) {
                setOpen(false);
              }
            }}
          >
            <ListItemIcon>{opt.icon || getKeyIcon(opt.iconKey!)}</ListItemIcon>
            <ListItemText primary={opt.label} />
          </ListItemButton>
        ))}
      </>
    );
  };  
  
  return smallViewport ? (
    <Dialog sx={{ marginTop: topBarHeight }} open={open} onClose={() => setOpen(false)} fullWidth={true}>
      {customSections ? (
        getMenuSectionList()
      ) : (
        <List>
          <div>{getMenuList()}</div>
        </List>
      )}
    </Dialog>
  ) : (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        "& .MuiDrawer-paper": {
          position: "relative",
          height: `calc(100vh - ${topBarHeight}px)`,
          overflow: "auto",
          overflowX: "hidden",
          whiteSpace: "nowrap",
          width: drawerWidth,
          marginTop: `${topBarHeight}px`,
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          boxSizing: "border-box",
          ...(open
            ? {}
            : {
                width: (theme) => theme.spacing(1),
                [theme.breakpoints.up("sm")]: {
                  width: (theme) => theme.spacing(7),
                },
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
              }),
        },
      }}
    >
      <Divider style={{color: "#E6E6E8"}} />
      {customSections ? (
        getMenuSectionList()
      ) : (
        <List>
          <div>{getMenuList()}</div>
        </List>
      )}
    </Drawer>

  );
}

